import { forwardRef, useImperativeHandle, useState } from 'react';
import { CompanyCustomer } from './CompanyCustomerList';
import { Col, Form, Row } from 'react-bootstrap';
import * as yup from 'yup';
import axiosInstance from 'utils/axiosInterceptors';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import { useLocationData } from './useGetLocationData';

type CreateCompanyFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const CreateCompanyForm = forwardRef<CreateCompanyFormHandle>((_, ref) => {
  const [companyDetails, setCompanyDetails] = useState<CompanyCustomer>({
    email: '',
    contactNumber: '',
    companyName: '',
    typeOfBusiness: '',
    taxIdentificationNumber: '',
    isRelatedToAutomotiveBusiness: false,
    paymentTerms: 0,
    address: {
      addressLine1: '',
      addressLine2: '',
      barangay: '',
      city: '',
      province: '',
      zipCode: ''
    },
    user: {
      email: '',
      firstName: '',
      lastName: '',
      mobileNumber: ''
    },
    membershipCardNumber: ''
  });
  const { provinces, cities, barangays, fetchCities, fetchBarangays } =
    useLocationData();

  const handleCompanyFormChange = <K extends keyof CompanyCustomer>(
    field: K,
    value: CompanyCustomer[K]
  ) => {
    setCompanyDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedCompanyFormChange = <
    K extends keyof CompanyCustomer,
    NK extends keyof NonNullable<CompanyCustomer[K]>
  >(
    field: K,
    nestedField: NK,
    value: NonNullable<CompanyCustomer[K]>[NK]
  ) => {
    setCompanyDetails(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] as object),
        [nestedField]: value
      }
    }));
  };

  const CreateCompanyFormSchema = yup.object().shape({
    email: yup
      .string()
      .email('Invalid email format'),
    contactNumber: yup.string(),
    companyName: yup.string().required('Company name is required'),
    typeOfBusiness: yup.string().required('Type of business is required'),
    taxIdentificationNumber: yup
      .string(),
    isRelatedToAutomotiveBusiness: yup
      .boolean()
      .required('Related to automotive business status is required'),
    paymentTerms: yup
      .number()
      .min(0, 'Payment terms must be a positive number')
      .required('Payment terms are required'),
    address: yup.object().shape({
      addressLine1: yup.string(),
      addressLine2: yup.string(),
      barangay: yup.string(),
      city: yup.string().required('City is required'),
      province: yup.string().required('Province is required'),
      zipCode: yup
        .string()
    }),
    user: yup.object().shape({
      email: yup
        .string()
        .email('Invalid user email format'),
      firstName: yup.string(),
      lastName: yup.string(),
      mobileNumber: yup
        .string()
    })
  });

  const handleSubmit = async () => {
    try {
      await CreateCompanyFormSchema.validate(companyDetails, {
        abortEarly: false
      });
      await axiosInstance.post(`/api/v1/customers/companies`, companyDetails);
      toast.success('Company Created', { ...toastConfig, autoClose: 2000 });
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <Form className="mb-3">
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Company Email</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.email}
            onChange={e => handleCompanyFormChange('email', e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Company Number</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.contactNumber}
            onChange={e =>
              handleCompanyFormChange('contactNumber', e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Company Name</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.companyName}
            onChange={e =>
              handleCompanyFormChange('companyName', e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Type of Business</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.typeOfBusiness}
            onChange={e =>
              handleCompanyFormChange('typeOfBusiness', e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Tax Identification Number</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.taxIdentificationNumber}
            onChange={e =>
              handleCompanyFormChange('taxIdentificationNumber', e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Is Related To Automotive Business</Form.Label>
          <Form.Control
            as="select"
            value={companyDetails.isRelatedToAutomotiveBusiness.toString()}
            onChange={e =>
              handleCompanyFormChange(
                'isRelatedToAutomotiveBusiness',
                e.target.value === 'true'
              )
            }
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Control>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Payment Terms</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.paymentTerms}
            onChange={e =>
              handleCompanyFormChange('paymentTerms', +e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Membership Number (Optional)</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.membershipCardNumber}
            onChange={e =>
              handleCompanyFormChange('membershipCardNumber', e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <hr></hr>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Address Line 1</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.address.addressLine1}
            onChange={e =>
              handleNestedCompanyFormChange(
                'address',
                'addressLine1',
                e.target.value
              )
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Address Line 2</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.address.addressLine2}
            onChange={e =>
              handleNestedCompanyFormChange(
                'address',
                'addressLine2',
                e.target.value
              )
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Province</Form.Label>
          <Form.Control
            as="select"
            value={companyDetails.address.province}
            onChange={e => {
              const selectedProvinceCode = e.target.value;
              const selectedProvince = provinces.find(
                province => province.name === selectedProvinceCode
              );

              if (selectedProvince) {
                handleNestedCompanyFormChange(
                  'address',
                  'province',
                  selectedProvince.name
                );
              }

              fetchCities(selectedProvince?.code as string);
            }}
          >
            <option value="">Select City</option>
            {provinces.map((province, index) => (
              <option key={index} value={province.name}>
                {province.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>City</Form.Label>
          <Form.Control
            as="select"
            value={companyDetails.address.city}
            onChange={e => {
              const selectedCity = e.target.value;
              const selectedCityDistrictCode = cities.find(
                city => city.name === selectedCity
              );

              handleNestedCompanyFormChange('address', 'city', e.target.value);
              fetchBarangays(
                selectedCityDistrictCode?.districtCode as string,
                selectedCityDistrictCode?.code as string
              );
            }}
          >
            <option value="">Select City</option>
            {cities.map((city, index) => (
              <option key={index} value={city.name}>
                {city.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group as={Col}>
          <Form.Label>Barangay</Form.Label>
          <Form.Control
            as="select"
            value={companyDetails.address.barangay}
            onChange={e =>
              handleNestedCompanyFormChange(
                'address',
                'barangay',
                e.target.value
              )
            }
          >
            <option value="">Select Barangay</option>
            {barangays.map((barangay, index) => (
              <option key={index} value={barangay.name}>
                {barangay.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        <Form.Group as={Col}>
          <Form.Label>Zip Code</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.address.zipCode}
            onChange={e =>
              handleNestedCompanyFormChange(
                'address',
                'zipCode',
                e.target.value
              )
            }
          />
        </Form.Group>
      </Row>
      <hr></hr>

      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.user.email}
            onChange={e =>
              handleNestedCompanyFormChange('user', 'email', e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.user.firstName}
            onChange={e =>
              handleNestedCompanyFormChange('user', 'firstName', e.target.value)
            }
          />
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <Form.Group as={Col}>
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.user.lastName}
            onChange={e =>
              handleNestedCompanyFormChange('user', 'lastName', e.target.value)
            }
          />
        </Form.Group>
        <Form.Group as={Col}>
          <Form.Label>Mobile Number</Form.Label>
          <Form.Control
            type="text"
            value={companyDetails.user.mobileNumber}
            onChange={e =>
              handleNestedCompanyFormChange(
                'user',
                'mobileNumber',
                e.target.value
              )
            }
          />
        </Form.Group>
      </Row>
    </Form>
  );
});

export default CreateCompanyForm;
